import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../assets/images/arka-saydam-logo.png';
import { logout } from '../lib/auth';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { API_BASE_URL } from '../config';
import { fetchProfessionalSummary } from '../utils/professionalSummary';

export default function Sidebar({ sidebarOpen, toggleSidebar, onStateChange }) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [shoppingCount, setShoppingCount] = useState(0);
  const [professionalSummary, setProfessionalSummary] = useState({
    isProfessional: false,
    professionalType: null,
    panelPath: null,
    activeClientCount: 0,
    activeClients: [],
  });
  const [userPermissions, setUserPermissions] = useState({
    isAdmin: false,
    isPremium: false,
    hasDietPremium: false,
    hasAthletePremium: false,
    hasAlakartPremium: false,
    hasAllergenPremium: false,
    hasSpecialDaysPremium: false,
    createdAt: null,
    lockedByTime: false,
    daysRemaining: 0,
  });
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMd = useMediaQuery('(min-width: 768px)');

  const isExpanded = isMd ? (!userCollapsed || hoverOpen) : true;

  useEffect(() => {
    if (onStateChange) {
      onStateChange(isExpanded);
    }
  }, [isExpanded, onStateChange]);

  const fetchUnread = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;
      const res = await fetch(`${API_BASE_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('authToken');
        return;
      }
      if (res.ok) {
        const data = await res.json();
        const unread = data.notifications?.filter((n) => !n.isRead).length || 0;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.warn('Sidebar notification fetch failed', err);
    }
  };

  const fetchShoppingCount = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;
      const res = await fetch(`${API_BASE_URL}/api/shopping/lists/counts`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('authToken');
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setShoppingCount(Number(data?.pendingCount || 0));
      }
    } catch (err) {
      console.warn('Sidebar shopping count fetch failed', err);
    }
  };

  const fetchPermissions = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setPermissionsLoaded(true);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('authToken');
        setPermissionsLoaded(true);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        const u = data.user || {};
        const entitlements = data.entitlements || {};
        const modules = entitlements.modules || {};
        setUserPermissions({
          isAdmin: !!u.isAdmin,
          isPremium: !!modules.global?.granted,
          hasDietPremium: !!modules.diet?.granted,
          hasAthletePremium: !!modules.athlete?.granted,
          hasAlakartPremium: !!modules.alakart?.granted,
          hasAllergenPremium: !!modules.allergen?.granted,
          hasSpecialDaysPremium: !!modules.special_days?.granted,
          createdAt: u.createdAt,
          lockedByTime: !!entitlements.lockedByTime,
          daysRemaining: Number(entitlements.daysRemaining || 0),
        });
      }
    } catch (err) {
      console.warn('Sidebar permissions fetch failed', err);
    } finally {
      setPermissionsLoaded(true);
    }
  };

  const fetchProfessionalInfo = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const summary = await fetchProfessionalSummary(authToken);
      setProfessionalSummary(summary);
    } catch (err) {
      console.warn('Sidebar professional summary fetch failed', err);
    }
  };

  useEffect(() => {
    const handleShoppingCountUpdated = (event) => {
      const nextCount = Number(event?.detail?.pendingCount);
      if (Number.isFinite(nextCount) && nextCount >= 0) {
        setShoppingCount(nextCount);
      }
    };

    fetchUnread();
    fetchPermissions();
    fetchShoppingCount();
    fetchProfessionalInfo();

    window.addEventListener('refreshNotifications', fetchUnread);
    window.addEventListener('profileUpdated', fetchPermissions);
    window.addEventListener('Alakart_SYNC', fetchShoppingCount);
    window.addEventListener('shopping:list-count-updated', handleShoppingCountUpdated);
    window.addEventListener('professionalUpdated', fetchProfessionalInfo);
    window.addEventListener('realtime:notifications-updated', fetchUnread);
    window.addEventListener('realtime:inventory-updated', fetchShoppingCount);
    window.addEventListener('realtime:force-refetch', fetchUnread);

    const bc = new BroadcastChannel('notifications_channel');
    bc.onmessage = (event) => {
      if (event.data === 'refresh') {
        fetchUnread();
        fetchShoppingCount();
        fetchProfessionalInfo();
      }
    };

    const interval = setInterval(() => {
      fetchUnread();
      fetchShoppingCount();
      fetchProfessionalInfo();
    }, 300000);

    return () => {
      window.removeEventListener('refreshNotifications', fetchUnread);
      window.removeEventListener('profileUpdated', fetchPermissions);
      window.removeEventListener('Alakart_SYNC', fetchShoppingCount);
      window.removeEventListener('shopping:list-count-updated', handleShoppingCountUpdated);
      window.removeEventListener('professionalUpdated', fetchProfessionalInfo);
      window.removeEventListener('realtime:notifications-updated', fetchUnread);
      window.removeEventListener('realtime:inventory-updated', fetchShoppingCount);
      window.removeEventListener('realtime:force-refetch', fetchUnread);
      bc.close();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (isMd && !e.target.closest('aside')) {
        setUserCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isMd]);

  useEffect(() => {
    if (!isMd && sidebarOpen) toggleSidebar();
  }, [location.pathname]);

  const mdWidth = '18rem';
  const mdCollapsed = '5rem';

  const style = isMd
    ? {
        width: isExpanded ? mdWidth : mdCollapsed,
        transform: 'translateX(0)',
      }
    : {
        width: '18rem',
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
      };

  const labelClass = (base = '') => {
    const visibility = isExpanded ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible -translate-x-4';
    return `font-medium whitespace-nowrap transition-all duration-300 ${isMd ? visibility : 'opacity-100 visible'} ${base}`;
  };

  const navItems = [
    { to: '/', icon: 'fa-house', label: 'Ana Sayfa' },
        { to: '/my-kitchen', icon: 'fa-kitchen-set', label: 'Mutfağım' },
        { to: '/quick-search', icon: 'fa-magnifying-glass', label: 'Hızlı Tarif Ara' },
    { to: '/all-recipes', icon: 'fa-book-open', label: 'Tüm Tarifler' },
    { to: '/my-favorites', icon: 'fa-heart', label: 'Favorilerim' },
        { to: '/smart-fridge', icon: 'fa-magnifying-glass-plus', label: 'Buzdolabımda Ara' },
    { to: '/my-shopping-lists', icon: 'fa-rectangle-list', label: 'Market Listelerim', count: shoppingCount },
        { to: '/kitchen-planner', icon: 'fa-calendar-check', label: 'Mutfak Planlayıcım', lockable: true },
    {
      to: '/alakart-menu',
      icon: 'fa-wand-magic-sparkles',
      label: 'Alakart Menü',
      premiumKey: 'hasAlakartPremium',
    },
    {
      to: '/allergen-kitchen',
      icon: 'fa-shield-halved',
      label: 'Alerjen Mutfağı',
      premiumKey: 'hasAllergenPremium',
    },
    {
      to: '/diet-kitchen',
      icon: 'fa-scale-balanced',
      label: 'Diyet Mutfağı',
      premiumKey: 'hasDietPremium',
    },
    {
      to: '/athlete-kitchen',
      icon: 'fa-dumbbell',
      label: 'Sporcu Mutfağı',
      premiumKey: 'hasAthletePremium',
    },
    {
      to: '/special-days-kitchen',
      icon: 'fa-cake-candles',
      label: 'Özel Günler Mutfağı',
      premiumKey: 'hasSpecialDaysPremium',
    },
    { to: '/allergen-scan', icon: 'fa-barcode', label: 'Alerjen Kontrol', premiumKey: 'hasAllergenPremium' },
  ];

  const filteredNavItems = navItems.filter((item) => {
    const hasModuleAccess = item.premiumKey ? !!userPermissions[item.premiumKey] : true;
    const isTimeLocked = !!((item.lockable || item.premiumKey) && userPermissions.lockedByTime && !userPermissions.isAdmin);
    return hasModuleAccess && !isTimeLocked;
  });

  const professionalNavItem = permissionsLoaded
    && professionalSummary.isProfessional
    && !userPermissions.isAdmin
    ? {
        to: professionalSummary.panelPath || '/diyetisyen-panel',
        icon: 'fa-user-doctor',
        label: professionalSummary.professionalType === 'pt' ? 'PT Panelim' : 'Diyetisyen Panelim',
        count: professionalSummary.activeClientCount,
      }
    : null;

  return (
    <>
      {!isMd && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] transition-opacity animate-in fade-in"
          onClick={toggleSidebar}
        />
      )}

      <aside
        onMouseEnter={() => isMd && setHoverOpen(true)}
        onMouseLeave={() => isMd && setHoverOpen(false)}
        onClick={() => isMd && setUserCollapsed(false)}
        style={style}
        className="group/sidebar no-scrollbar fixed left-0 top-0 z-[160] flex h-screen flex-col overflow-y-auto overflow-x-hidden border-r border-[#e6dbdb] dark:border-gray-800 bg-white dark:bg-[#1a1a1a] transition-all duration-300 shadow-2xl"
      >
        <div className="flex h-20 items-center gap-4 px-5 shrink-0 border-b border-dashed border-[#e6dbdb] dark:border-gray-800">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white dark:bg-[#0b0b0b] shadow-sm ring-1 ring-black/5">
            <img src={Logo} alt="Logo" className="h-8 w-8 object-contain" />
          </div>
          <div className={`flex flex-col transition-all duration-300 ${isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
            <h1 className="text-lg font-bold leading-tight text-gray-900 dark:text-white">Bana Bir Tarif Ver</h1>
            <p className="text-[10px] font-bold text-slate-500 dark:text-gray-400">Akıllı Mutfak Asistanı</p>
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-2 p-3">
          {filteredNavItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-4 rounded-lg p-3 transition-colors ${
                location.pathname === item.to
                  ? 'bg-red-50 text-red-600 dark:bg-red-900/10 dark:text-red-400'
                  : 'text-gray-900 dark:text-white hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/10'
              }`}
            >
              <i className={`fa-solid ${item.icon} shrink-0 text-[24px]`} />
              <span className={labelClass()}>{item.label}</span>
              {item.count > 0 && isExpanded && (
                <span className="ml-auto rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">{item.count}</span>
              )}
            </Link>
          ))}

          {professionalNavItem && (
            <Link
              to={professionalNavItem.to}
              className={`flex items-center gap-4 rounded-lg p-3 transition-colors ${
                location.pathname === professionalNavItem.to
                  ? 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300'
                  : 'text-cyan-700 dark:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/10'
              }`}
            >
              <i className={`fa-solid ${professionalNavItem.icon} shrink-0 text-[24px]`} />
              <span className={labelClass()}>{professionalNavItem.label}</span>
              {professionalNavItem.count > 0 && isExpanded && (
                <span className="ml-auto rounded-full bg-cyan-600 px-2 py-0.5 text-[10px] font-bold text-white">
                  {professionalNavItem.count}
                </span>
              )}
            </Link>
          )}
        </nav>

        {permissionsLoaded && professionalSummary.isProfessional && !userPermissions.isAdmin && isExpanded && (
          <div className="mx-3 mb-2 rounded-xl border border-cyan-100 bg-cyan-50/80 p-3 dark:border-cyan-900/40 dark:bg-cyan-900/10">
            <div className="text-[11px] font-bold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">Takip Ettiğin Danışanlar</div>
            <div className="mt-1 text-sm font-bold text-cyan-900 dark:text-cyan-100">{professionalSummary.activeClientCount} aktif danışan</div>
            <ul className="mt-2 space-y-1">
              {professionalSummary.activeClients.length === 0 ? (
                <li className="text-xs text-cyan-700/80 dark:text-cyan-300/80">Henüz aktif danışan yok.</li>
              ) : (
                professionalSummary.activeClients.map((client) => (
                  <li key={client.id} className="text-xs text-cyan-800 dark:text-cyan-200 truncate">
                    {client.name}
                  </li>
                ))
              )}
            </ul>
          </div>
        )}

        <div className="mt-auto border-t border-dashed border-[#e6dbdb] dark:border-gray-800 p-3">
          <Link to="/profile" className="flex items-center gap-4 rounded-lg p-3 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <i className="fa-solid fa-user-gear shrink-0 text-[24px]" />
            <span className={labelClass()}>Ayarlar</span>
          </Link>
          <button
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="flex w-full items-center gap-4 rounded-lg p-3 text-red-600 hover:bg-red-50 transition-colors"
          >
            <i className="fa-solid fa-right-from-bracket shrink-0 text-[24px]" />
            <span className={labelClass()}>Çıkış Yap</span>
          </button>
        </div>
      </aside>
    </>
  );
}

